import axios from 'axios';
import { useState } from 'react';
import {
  Button, Header, Input, Loader, Table,
  TableBody, TableCell,
  TableHeader, TableHeaderCell, TableRow
} from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { useStore } from '../../../app/stores/store';
import moment from 'moment';

interface HCHours {
  date: string;
  cluster_Name: string;
  emp_id: string;
  emp_name: string;
  working_hours: string;
  extra_hours: string;
  action_status: string;
}

const MonthlyManualHoursAdd = () => {
  const { userStore: {user} } = useStore();
  const [loading, setLoading] = useState(false);
  const [loadData, setLoadData] = useState<HCHours[]>([]);
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(new Date());
  const [empId, setEmpId] = useState('');

  const fetchData = async () => {
    if (!empId) {
      toast.error('Please enter Employee ID');
      return;
    }

    if (fromDate > toDate) {
      toast.error('From Date cannot be after To Date');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get("/Hour/EmployeeWorkingHoursMonthly", {
        params: {
          startDate: fromDate,
          endDate: toDate,
          EmployeeId: empId,
        }
      });
      setLoadData(data);
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        (error as any).response &&
        (error as any).response.status === 404 &&
        (error as any).response.data === 'No data found'
      ) {
        setLoadData([]);
        toast.warning('No data available for selected date range');
      } else {
        toast.error("Failed to fetch data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModifyClick = (empId: string, dateString: string) => {
    const parsedDate = moment(dateString, 'DD-MM-YYYY');

    if (!parsedDate.isValid()) {
      console.error('Invalid date format received:', dateString);
      return;
    }

    const formattedDate = parsedDate.format('MM/DD/YYYY');
    const queryParams = new URLSearchParams({
      employeeId: empId,
      selectedDate: formattedDate
    });
    window.open(`/ManualHoursAdd?${queryParams.toString()}`, '_blank');
  };

  return (
    <div className='title_wise_cutlistreport'>
      <Header as="h2">Monthly Hour Update</Header>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginTop: 20 }}>
        <label>From Date</label>
        <DatePicker
          selected={fromDate}
          onChange={(date: Date) => setFromDate(date)}
          className="date-picker-style"
        />
        <label>To Date</label>
        <DatePicker
          selected={toDate}
          onChange={(date: Date) => setToDate(date)}
          className="date-picker-style"
          minDate={fromDate}
        />
        <label>Employee Id</label>
        <Input value={empId} onChange={(e) => setEmpId(e.target.value)} />
        <Button primary onClick={fetchData}>Submit</Button>
      </div>

      <div style={{ maxHeight: '75vh', overflowY: 'auto', marginTop: '50px', marginBottom: '50px' }}>
        <Table celled>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Date</TableHeaderCell>
              <TableHeaderCell>Emp Id</TableHeaderCell>
              <TableHeaderCell>Emp Name</TableHeaderCell>
              <TableHeaderCell>Cluster Name</TableHeaderCell>
              <TableHeaderCell>Working Hours</TableHeaderCell>
              <TableHeaderCell>Extra Hours</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              {(user?.roleName === 'Resto Admin') && (
              <TableHeaderCell>Modify</TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>

          <TableBody>
            {!loading && loadData.length > 0 ? (
              loadData.map((data, idx) => (
                <TableRow key={idx}>
                  <TableCell>{data.date}</TableCell>
                  <TableCell>{data.emp_id}</TableCell>
                  <TableCell>{data.emp_name}</TableCell>
                  <TableCell>{data.cluster_Name}</TableCell>
                  <TableCell 
                      style={{
                              color: (() => {
                                const [hours, minutes] = (data.working_hours || "00:00").split(':').map(Number);
                                const decimalHours = hours + (minutes / 60);
                                return decimalHours !== 7.5 ? 'red' : 'inherit';
                              })()
                            }}
                  >{data.working_hours}</TableCell>
                  <TableCell>{data.extra_hours}</TableCell>
                  <TableCell>{data.action_status}</TableCell>
                  {(user?.roleName === 'Resto Admin') && (
                  <TableCell>
                    <Button icon="external alternate" basic color="yellow" onClick={() => handleModifyClick(data.emp_id, data.date)}/>
                  </TableCell>
                  )}
                </TableRow>
              ))
            ) : loading ? (
              <TableRow>
                <TableCell colSpan="10" textAlign="center">
                  <Loader active inline="centered">Loading</Loader>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan="10" textAlign="center">No data found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default MonthlyManualHoursAdd;
